import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import {
  getGrupoDetailThunk,
  getPermisosDisponiblesThunk,
  syncGrupoPermisosThunk,
} from "../../store/rolesPermisos/rolesPermisos.thunks";
import { clearGrupoDetail, clearError } from "../../store/rolesPermisos/rolesPermisos.slice";
import Loader from "../UI/Loader";

const GrupoPermisosModal = ({ grupo, onClose }) => {
  const dispatch = useDispatch();
  const { grupoDetail, grupoDetailLoading, permisosDisponibles, permisosLoading, syncGrupoLoading, error } =
    useSelector((s) => s.rolesPermisos);

  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    dispatch(getGrupoDetailThunk(grupo.id));
    if (!permisosDisponibles.length) {
      dispatch(getPermisosDisponiblesThunk());
    }
    return () => dispatch(clearGrupoDetail());
  }, [dispatch, grupo.id]);

  useEffect(() => {
    if (grupoDetail) {
      setSelected(new Set((grupoDetail.permissions ?? []).map((p) => p.id)));
    }
  }, [grupoDetail]);

  const totalDisponibles = useMemo(
    () => permisosDisponibles.reduce((acc, g) => acc + g.permisos.length, 0),
    [permisosDisponibles]
  );

  const filteredApps = useMemo(() => {
    if (!search.trim()) return permisosDisponibles;
    const q = search.toLowerCase();
    return permisosDisponibles
      .map((app) => ({
        ...app,
        permisos: app.permisos.filter(
          (p) => p.codename.includes(q) || p.name.toLowerCase().includes(q)
        ),
      }))
      .filter((app) => app.permisos.length > 0);
  }, [permisosDisponibles, search]);

  const togglePerm = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSuccess(false);
  };

  const toggleAll = () => {
    const allIds = permisosDisponibles.flatMap((a) => a.permisos.map((p) => p.id));
    if (selected.size === allIds.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
    setSuccess(false);
  };

  const toggleApp = (appPermisos) => {
    const ids = appPermisos.map((p) => p.id);
    const allChecked = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
    setSuccess(false);
  };

  const handleSave = async () => {
    dispatch(clearError());
    const result = await dispatch(
      syncGrupoPermisosThunk({ id: grupo.id, permissionIds: [...selected] })
    );
    if (!result.error) setSuccess(true);
  };

  const isLoading = grupoDetailLoading || permisosLoading;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <Icon icon="mdi:shield-key-outline" className="me-2" />
              Permisos del grupo: <strong>{grupo.name}</strong>
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {isLoading ? (
              <Loader center text="Cargando permisos..." delay={0} />
            ) : (
              <>
                {error && (
                  <div className="alert alert-danger py-2">{error}</div>
                )}
                {success && (
                  <div className="alert alert-success py-2">
                    <Icon icon="mdi:check-circle" className="me-1" />
                    Permisos guardados correctamente.
                  </div>
                )}

                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="flex-grow-1">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Buscar permiso..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={toggleAll}
                  >
                    {selected.size === totalDisponibles ? "Quitar todos" : "Seleccionar todos"}
                  </button>
                  <span className="badge bg-primary-100 text-primary-600 fw-semibold">
                    {selected.size} / {totalDisponibles} seleccionados
                  </span>
                </div>

                <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
                  {filteredApps.map((appGroup) => {
                    const appIds = appGroup.permisos.map((p) => p.id);
                    const allAppChecked = appIds.every((id) => selected.has(id));
                    const someAppChecked = appIds.some((id) => selected.has(id));

                    return (
                      <div key={appGroup.app} className="mb-3">
                        <div
                          className="d-flex align-items-center gap-2 p-2 rounded cursor-pointer"
                          style={{ background: "#f8f9fa", userSelect: "none" }}
                          onClick={() => toggleApp(appGroup.permisos)}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            checked={allAppChecked}
                            ref={(el) => {
                              if (el) el.indeterminate = someAppChecked && !allAppChecked;
                            }}
                            onChange={() => {}}
                          />
                          <span className="fw-semibold text-primary-600 text-sm">
                            {appGroup.app}
                          </span>
                          <span className="badge bg-neutral-200 text-neutral-600 ms-auto">
                            {appIds.filter((id) => selected.has(id)).length} / {appIds.length}
                          </span>
                        </div>

                        <div className="row g-1 ms-1 mt-1">
                          {appGroup.permisos.map((perm) => (
                            <div key={perm.id} className="col-12 col-md-6 col-lg-4">
                              <label
                                className="d-flex align-items-start gap-2 p-2 rounded cursor-pointer"
                                style={{
                                  fontSize: "0.8rem",
                                  border: "1px solid",
                                  borderColor: selected.has(perm.id) ? "#7749F8" : "#e5e7eb",
                                  background: selected.has(perm.id) ? "#f5f0ff" : "white",
                                  transition: "all 0.15s",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-0 flex-shrink-0"
                                  checked={selected.has(perm.id)}
                                  onChange={() => togglePerm(perm.id)}
                                />
                                <span>
                                  <span className="d-block text-neutral-500" style={{ fontSize: "0.7rem", fontFamily: "monospace" }}>
                                    {perm.codename}
                                  </span>
                                  <span className="text-neutral-700">{perm.name}</span>
                                </span>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>
              Cerrar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={syncGrupoLoading || isLoading}
            >
              {syncGrupoLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:content-save-outline" className="me-1" />
                  Guardar permisos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrupoPermisosModal;
